import {
  ConnectionVariant,
  InstallableUserIntegrationSchema,
  IntegrationSchema,
} from '../../definitions/schemas';
import {
  integrationProviderLogos,
  integrationProviderNames,
  IntegrationVersion,
  IntegrationProvider,
} from '../../enums/Integrations/IntegrationProvider';
import { IntegrationType } from '../../enums/Integrations/IntegrationType';
import RolesEnum from '../../enums/Roles';
import { canDo } from '../../Services/GuardPermissionsService';

export const getCrmIntegrationConnected = (
  installedIntegrations: IntegrationSchema[]
) =>
  installedIntegrations.find(
    (integration) => integration.integrationType === IntegrationType.CRM
  );

export const getCrmConnectedData = (
  installedIntegrations: IntegrationSchema[]
): [string, React.FunctionComponent<React.SVGProps<SVGSVGElement>> | null] => {
  const integration = getCrmIntegrationConnected(installedIntegrations);

  if (!integration) {
    return ['', null];
  }

  return [
    integrationProviderNames[integration.providerName],
    integrationProviderLogos[integration.providerName],
  ];
};

export const isMicrosoftDynamicsV3 = (
  integration?: IntegrationSchema | null
): boolean =>
  integration?.providerName === IntegrationProvider.MICROSOFT_DYNAMICS &&
  integration?.version === IntegrationVersion.V3;

const isCrmConnectedVersionEnabled = (
  installedIntegrations: IntegrationSchema[],
  isVersionFlagEnabled: boolean,
  version: IntegrationVersion
): boolean => {
  const integration = getCrmIntegrationConnected(installedIntegrations);

  if (!integration || integration.deactivated || !isVersionFlagEnabled) {
    return false;
  }

  return integration.version === version;
};

export const isCrmConnectedV2Enabled = (
  installedIntegrations: IntegrationSchema[],
  areCRMIntegrationEnabled: boolean
): boolean =>
  isCrmConnectedVersionEnabled(
    installedIntegrations,
    areCRMIntegrationEnabled,
    IntegrationVersion.V2
  );

export const isCrmConnectedV3Enabled = (
  installedIntegrations: IntegrationSchema[],
  isCrmAbstractionMsDynamicsEnabled: boolean
): boolean =>
  isCrmConnectedVersionEnabled(
    installedIntegrations,
    isCrmAbstractionMsDynamicsEnabled,
    IntegrationVersion.V3
  );

export const resolveUserSfdcCrmConnectionStatus = ({
  installedUserIntegrations,
  installableUserIntegrations,
  installedIntegrations,
}: {
  installedUserIntegrations: IntegrationSchema[];
  installableUserIntegrations: InstallableUserIntegrationSchema[];
  installedIntegrations: IntegrationSchema[];
}) => {
  const isOrgSfdcConnected = installedIntegrations.some(
    (integration) => integration.providerName === IntegrationProvider.SALESFORCE
  );
  if (!isOrgSfdcConnected) {
    return {
      isOrgSfdcConnected: false,
      isUserCrmConnected: false,
    };
  }

  const isUserSfdcConnected = installedUserIntegrations.some(
    (integration) => integration.providerName === IntegrationProvider.SALESFORCE
  );

  if (!isUserSfdcConnected) {
    const sfdcInstallable = installableUserIntegrations.filter(
      (integration) =>
        integration.providerName === IntegrationProvider.SALESFORCE
    );
    return {
      isOrgSfdcConnected: true,
      isUserCrmConnected: false,
      installUrl:
        sfdcInstallable.find(
          (i) => i.connectionVariant === ConnectionVariant.PRIMARY
        )?.installUrl ?? null,
      partnerInstallUrl:
        sfdcInstallable.find(
          (i) => i.connectionVariant === ConnectionVariant.PARTNER
        )?.installUrl ?? null,
    };
  }
  return {
    isOrgSfdcConnected: true,
    isUserCrmConnected: true,
  };
};

// True when SFDC user scoping is gated on for release and the current user is Sales; use to gate CRM creation and local CRM search.
export const shouldApplySalesCrmRestrictions = (
  isReleaseControlSfdcUserScoping: boolean
) => isReleaseControlSfdcUserScoping && canDo(RolesEnum.SALES);
